import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Shield, Award, Globe } from 'lucide-react';
import Slider from '../../slider/Slider';
import Blueberries from '../../../assets/photos/blueberries.jpg';
import Mandarins from '../../../assets/photos/mandarins.jpg';
import Palta from '../../../assets/photos/palta.jpg';
import Palta2 from '../../../assets/photos/palta2.jpg';
import Pommegrennate from '../../../assets/photos/pommegrenate.jpg';

// Define the props interface
interface NetworkProps {
  onClickGetStarted: () => void;
}

const Network: React.FC<NetworkProps> = ({ onClickGetStarted }) => {
  const { t } = useTranslation();
  
  const images: string[] = [
    Blueberries,
    Mandarins,
    Palta,
    Palta2,
    Pommegrennate
  ];

  const features = [
    { icon: Shield, text: t('network.features.certified') },
    { icon: Award, text: t('network.features.expert') },
    { icon: Globe, text: t('network.features.global') }
  ];

  return (
    <div className='relative w-full bg-gradient-to-br from-[#f84831] via-[#ff6b54] to-[#f84831] py-16 md:py-20 px-4 overflow-hidden'>
      <div className='max-w-7xl mx-auto grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center'>
        
        {/* Left Column - Text Content */}
        <motion.div 
          className='flex flex-col justify-center text-center lg:text-left space-y-6 md:space-y-8'
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >

          {/* Main Title */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <h2 className='text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 leading-tight text-white'>
              {t('network.title')}
            </h2>
            <div className="h-1 w-16 md:w-24 bg-white mb-4 md:mb-6 mx-auto lg:mx-0 rounded-full"></div>
          </motion.div>

          {/* Description */}
          <motion.p 
            className='text-base md:text-lg lg:text-xl xl:text-2xl font-medium text-white/90 leading-relaxed max-w-2xl mx-auto lg:mx-0 px-4 lg:px-0'
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            {t('network.description')}
          </motion.p>

          {/* Feature Pills */}
          <motion.div 
            className="flex flex-wrap gap-2 md:gap-3 justify-center lg:justify-start px-4 lg:px-0"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
          >
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="flex items-center px-3 md:px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white font-medium shadow-sm border border-white/30 hover:bg-white/30 transition-all duration-300 text-xs md:text-sm"
                whileHover={{ scale: 1.05 }}
              >
                <feature.icon className="w-3 md:w-4 h-3 md:h-4 mr-1 md:mr-2 text-white" />
                <span className="hidden sm:inline">{feature.text}</span>
                <span className="sm:hidden">{feature.text.split(' ')[0]}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Button */}
          <motion.div 
            className="flex justify-center lg:justify-start pt-2 md:pt-4 px-4 lg:px-0"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
          >
            <button 
              onClick={onClickGetStarted}
              className='group relative overflow-hidden bg-white hover:bg-gray-100 text-[#f84831] px-6 md:px-8 py-3 md:py-4 rounded-xl font-semibold text-base md:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl'
            >
              <span className="relative z-10 flex items-center">
                {t('network.getStarted')}
                <ChevronRight className="w-4 md:w-5 h-4 md:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          </motion.div>
        </motion.div>

        {/* Right Column - Enhanced Slider */}
        <motion.div 
          className='relative'
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <div className="relative">
            {/* Main Slider Container */}
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <Slider images={images} autoSlide={true} />
              
              {/* Overlay Gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none"></div>
            </div>


          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Network;
